/**
 * AIコントローラー
 *
 * AIプレイヤーの行動制御を担当
 * - ゲーム状態の取得
 * - アクション可能判定
 * - フェイズごとの行動実行
 * - 特殊ケース（手札選択、ターゲット選択等）の処理
 */

import { getStrategy } from './strategies';
import { statusEffectEngine } from '../statusEffects';
import { isSkillEffective } from './skillEffectivityChecker';
import { filterEffectiveMagicCards } from './magicEffectivityChecker';
import type { CardWithId, CardData, MonsterInstance, PhaseCardInstance, FieldCardInstance, HandCard } from '../../types/card';
import type { StatusEffect } from '../../types/effect';
import type {
  AIGameState,
  AIStrategy,
  UsableSkill,
  ChargeableMonster,
  ActionsRemaining,
  ChainContext,
} from './strategies/base';
import type { PlayerNumber } from '../../types/player';
import type { DeepSeaZone, DeepSeaCard, SubmergeContext } from '../deepSeaZone/types';
import { canSubmerge, getSurfaceableCards } from '../deepSeaZone/deepSeaManager';

/** AI思考速度の種類 */
export type AISpeedType = 'normal' | 'fast' | 'veryFast';

/** プレイヤータイプ */
export type PlayerType = 'human' | 'ai';

/** AI難易度 */
export type AIDifficulty = 'easy' | 'normal' | 'hard' | 'evolved' | 'evolvedSim';

/** コスト修正関数型 */
export type CostModifierFn = (card: CardWithId) => number;

/** プレイヤーデッキ設定 */
export interface PlayerDeckSettings {
  p1PlayerType: PlayerType;
  p2PlayerType: PlayerType;
  p1AIDifficulty: AIDifficulty;
  p2AIDifficulty: AIDifficulty;
  p1SelectedDeck: string;
  p2SelectedDeck: string;
  summoningSickness: boolean;
  secondPlayerBonusSP: boolean;
  piercingDamage: boolean;
  loneWarriorRule: boolean;
  initialLifePoints: number;
}

/** ゲーム状態データ（createAIGameState入力用） */
export interface GameStateData {
  phase: number;
  turn: number;
  isFirstTurn: boolean;
  firstPlayer?: PlayerNumber;
  p1Life: number;
  p2Life: number;
  p1Hand: CardWithId[];
  p2Hand: CardWithId[];
  p1Field: (MonsterInstance | null)[];
  p2Field: (MonsterInstance | null)[];
  p1Graveyard: CardWithId[];
  p2Graveyard: CardWithId[];
  p1Deck: CardWithId[];
  p2Deck: CardWithId[];
  p1ActiveSP: number;
  p2ActiveSP: number;
  p1RestedSP: number;
  p2RestedSP: number;
  p1BonusSP?: number;
  p2BonusSP?: number;
  p1FieldCard: FieldCardInstance | CardData | null;
  p2FieldCard: FieldCardInstance | CardData | null;
  p1PhaseCard: PhaseCardInstance | null;
  p2PhaseCard: PhaseCardInstance | null;
  p1StatusEffects?: StatusEffect[];
  p2StatusEffects?: StatusEffect[];
  // 深海領域
  p1DeepSeaZone?: DeepSeaZone | null;
  p2DeepSeaZone?: DeepSeaZone | null;
}

/** メインフェイズアクション種別 */
export interface MainPhaseActions {
  summonCard: (card: CardWithId, slot: number) => void;
  executeSkill: (monsterIndex: number, skillType: 'basic' | 'advanced') => void;
  activateMagicCard?: (card: CardWithId) => void;
  activateTrigger?: (trigger: unknown) => void;
  chargeCard?: (card: CardWithId, monsterIndex: number) => void;
  chargePhaseCard?: (card: CardWithId) => void;
  placeFieldCard?: (card: CardWithId) => void;
  placePhaseCard?: (card: CardWithId) => void;
  nextPhase: () => void;
}

/** 攻撃対象タイプ */
export type AttackTarget = number | 'direct';

/** バトルフェイズアクション種別 */
export interface BattlePhaseActions {
  attack: (attackerIndex: number, target: AttackTarget) => void;
  nextPhase: () => void;
}

/** メインフェイズオプション */
export interface MainPhaseOptions {
  costModifierFn?: CostModifierFn;
  activatableTriggers?: unknown[];
  chargeUsedThisTurn?: boolean;
}

/** 手札選択ペンディング情報 */
export interface PendingHandSelection {
  callback: (cards: CardWithId[] | null) => void;
  filter?: (card: CardWithId) => boolean;
  message?: string;
}

/** モンスターターゲット選択ペンディング情報 */
export interface PendingMonsterTarget {
  callback: (index: number | null) => void;
  targetPlayer: 'opponent' | 'self' | 'both';
  message?: string;
}

/** 墓地選択ペンディング情報 */
export interface PendingGraveyardSelection {
  callback: (cards: CardWithId[] | null) => void;
  filter?: (card: CardWithId) => boolean;
  message?: string;
}

/** デッキ確認ペンディング情報 */
export interface PendingDeckReview {
  cards: CardWithId[];
  onConfirm?: (cards: CardWithId[]) => void;
  onSelect?: (selectedCards: CardWithId[], remainingCards: CardWithId[]) => void;
  selectMode?: boolean;
}

/** チェーン確認情報 */
export interface ChainConfirmation {
  context?: ChainContext;
}

/** チェーンアクション */
export interface ChainActions {
  skipChainConfirmation: () => void;
  activateSetsunaInChain: (card: CardWithId) => void;
}

/**
 * 召喚前に使用すべき魔法カードのIDリスト
 * - リクルート系: デッキから直接場に出す（召喚の代わりになる）
 * - 蘇生系: 墓地から場に出す（召喚の代わりになる）
 * - 儀式系: 場のモンスターをリリースして上位を出す
 *
 * これ以外の魔法（ダメージ、バフ、サーチ等）は召喚後に使用
 */
const PRE_SUMMON_MAGIC_IDS = new Set([
  // リクルート系（条件なし）
  'C0000017', // 未来の粘液回路 - デッキから粘液獣・キング等を場に
  'C0000035', // ドラゴンの血脈 - デッキから高コストドラゴンを場に
  'C0000066', // ホーリーライトサモン - デッキからコスト3以下光属性を場に
  'C0000151', // ブリザードの呼び声 - デッキからコスト3以下ブリザードキャットを場に
  'C0000173', // 熔岩の呼び声 - デッキからコスト3以下マグマフォージを場に
  'C0000196', // 援軍の呼び声 - デッキからコスト3以下モンスターを場に
  'C0000253', // 未来の紫翼 - デッキからコスト3以下未来属性を場に
  'C0000201', // 地獄開城の契約 - デッキから3体まで場に

  // 儀式系（場のモンスターをリリースして上位を出す）
  'C0000088', // ネクロフィアの儀式 - 1体リリースしてダーク・ネクロフィアを場に
  'C0000120', // 禁忌の儀式 - 2体リリースしてブラック・オラクルを場に
  'C0000341', // ヴェルゼファール降臨の儀式 - ヴェルゼファールをリリースして上位を場に
  'C0000372', // 天翼の召喚儀式 - 鳥民をリリースしてヴォランティスを場に
  'C0000382', // 虹羽の覚醒脈 - 虹羽密林2体以上でヴォランティスを場に
]);

/**
 * AI思考の遅延時間（ミリ秒）
 */
export const AI_DELAY = {
  SHORT: 300,   // 短い遅延（選択確定等）
  MEDIUM: 500,  // 中程度の遅延（アクション間）
  LONG: 800,    // 長い遅延（フェイズ開始時）
};

/** AI思考速度設定の項目 */
interface AISpeedSetting {
  label: string;
  multiplier: number;
}

/**
 * AI思考速度の設定
 * - normal: 普通（1.0倍）
 * - fast: 早い（0.4倍）
 * - veryFast: とても速い（0.1倍）
 */
export const AI_SPEED_SETTINGS: Record<AISpeedType, AISpeedSetting> = {
  normal: { label: '普通', multiplier: 1.0 },
  fast: { label: '早い', multiplier: 0.4 },
  veryFast: { label: 'とても速い', multiplier: 0.1 },
};

const AI_SPEED_STORAGE_KEY = 'magicSpirit_aiThinkingSpeed';

/**
 * AI思考速度をlocalStorageから取得
 */
export function getAIThinkingSpeed(): AISpeedType {
  try {
    const stored = localStorage.getItem(AI_SPEED_STORAGE_KEY);
    if (stored && stored in AI_SPEED_SETTINGS) {
      return stored as AISpeedType;
    }
  } catch {
    // localStorageが使えない環境
  }
  return 'normal';
}

/**
 * AI思考速度をlocalStorageに保存
 */
export function setAIThinkingSpeed(speed: AISpeedType): void {
  try {
    if (speed in AI_SPEED_SETTINGS) {
      localStorage.setItem(AI_SPEED_STORAGE_KEY, speed);
    }
  } catch {
    // localStorageが使えない環境
  }
}

/**
 * 現在の速度倍率を取得
 */
export function getSpeedMultiplier(): number {
  const speed = getAIThinkingSpeed();
  return AI_SPEED_SETTINGS[speed]?.multiplier || 1.0;
}

/**
 * 速度倍率を適用した遅延時間を取得
 */
export function getScaledDelay(baseMs: number): number {
  return Math.round(baseMs * getSpeedMultiplier());
}

/**
 * 遅延を挿入（人間らしさのため）
 */
export function delay(ms: number): Promise<void> {
  const multiplier = getSpeedMultiplier();
  const adjustedMs = ms * multiplier;
  const randomVariance = Math.random() * 200 * multiplier;
  return new Promise(resolve => setTimeout(resolve, adjustedMs + randomVariance));
}

// ============================================================
// プレイヤーデッキ設定の保存・取得
// ============================================================

const PLAYER_DECK_SETTINGS_STORAGE_KEY = 'magicSpirit_playerDeckSettings';

/**
 * プレイヤーデッキ設定のデフォルト値
 */
const DEFAULT_PLAYER_DECK_SETTINGS: PlayerDeckSettings = {
  p1PlayerType: 'human',
  p2PlayerType: 'ai',
  p1AIDifficulty: 'normal',
  p2AIDifficulty: 'normal',
  p1SelectedDeck: 'starter',
  p2SelectedDeck: 'random',
  summoningSickness: false, // 召喚酔い（デフォルト: OFF）
  secondPlayerBonusSP: false, // 後攻ボーナスSP（デフォルト: OFF）
  piercingDamage: false, // 貫通ダメージ（デフォルト: OFF）
  loneWarriorRule: true, // 孤軍奮闘（デフォルト: ON）
  initialLifePoints: 6000, // 初期LP（デフォルト: 6000）
};

/**
 * プレイヤーデッキ設定をlocalStorageから取得
 */
export function getPlayerDeckSettings(): PlayerDeckSettings {
  try {
    const stored = localStorage.getItem(PLAYER_DECK_SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<PlayerDeckSettings>;
      // デフォルト値とマージ（新しいフィールドがある場合に対応）
      return { ...DEFAULT_PLAYER_DECK_SETTINGS, ...parsed };
    }
  } catch {
    // localStorageが使えない環境、またはパースエラー
  }
  return { ...DEFAULT_PLAYER_DECK_SETTINGS };
}

/**
 * プレイヤーデッキ設定をlocalStorageに保存
 */
export function savePlayerDeckSettings(settings: Partial<PlayerDeckSettings>): void {
  try {
    const current = getPlayerDeckSettings();
    const updated = { ...current, ...settings };
    localStorage.setItem(PLAYER_DECK_SETTINGS_STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // localStorageが使えない環境
  }
}

// ============================================================
// ゲーム状態取得ヘルパー
// ============================================================

/**
 * AIGameState を作成
 * 現在のプレイヤー視点でゲーム状態をまとめる
 */
export function createAIGameState(
  currentPlayer: PlayerNumber,
  gameStateData: GameStateData
): AIGameState {
  const {
    phase, turn, isFirstTurn,
    p1Life, p2Life,
    p1Hand, p2Hand,
    p1Field, p2Field,
    p1Graveyard, p2Graveyard,
    p1Deck, p2Deck,
    p1ActiveSP, p2ActiveSP,
    p1RestedSP, p2RestedSP,
    p1BonusSP, p2BonusSP,
    p1FieldCard, p2FieldCard,
    p1PhaseCard, p2PhaseCard,
    p1StatusEffects, p2StatusEffects,
    p1DeepSeaZone, p2DeepSeaZone,
  } = gameStateData;

  const isP1 = currentPlayer === 1;

  return {
    currentPlayer,
    phase,
    turn,
    isFirstTurn,
    firstPlayer: gameStateData.firstPlayer,

    myLife: isP1 ? p1Life : p2Life,
    opponentLife: isP1 ? p2Life : p1Life,
    myHand: isP1 ? p1Hand : p2Hand,
    opponentHandCount: isP1 ? p2Hand.length : p1Hand.length,
    myField: isP1 ? p1Field : p2Field,
    opponentField: isP1 ? p2Field : p1Field,
    myGraveyard: isP1 ? p1Graveyard : p2Graveyard,
    opponentGraveyard: isP1 ? p2Graveyard : p1Graveyard,
    myDeck: isP1 ? p1Deck : p2Deck,
    opponentDeck: isP1 ? p2Deck : p1Deck,
    myDeckCount: isP1 ? p1Deck.length : p2Deck.length,
    opponentDeckCount: isP1 ? p2Deck.length : p1Deck.length,

    myActiveSP: isP1 ? p1ActiveSP : p2ActiveSP,
    myRestedSP: isP1 ? p1RestedSP : p2RestedSP,
    myBonusSP: isP1 ? (p1BonusSP || 0) : (p2BonusSP || 0),
    myFieldCard: isP1 ? p1FieldCard : p2FieldCard,
    myPhaseCard: isP1 ? p1PhaseCard : p2PhaseCard,
    opponentFieldCard: isP1 ? p2FieldCard : p1FieldCard,
    opponentPhaseCard: isP1 ? p2PhaseCard : p1PhaseCard,
    myStatusEffects: isP1 ? (p1StatusEffects || []) : (p2StatusEffects || []),
    // 深海領域
    myDeepSeaZone: isP1 ? (p1DeepSeaZone || null) : (p2DeepSeaZone || null),
    opponentDeepSeaZone: isP1 ? (p2DeepSeaZone || null) : (p1DeepSeaZone || null),
  };
}

/** カード型（tempCostModifier付き） */
interface CardWithTempModifier extends CardWithId {
  tempCostModifier?: number;
}

/**
 * 召喚可能なカードを取得
 */
export function getSummonableCards(
  gameState: AIGameState,
  costModifierFn: CostModifierFn | null = null
): CardWithId[] {
  const { myHand, myField, myActiveSP, myBonusSP } = gameState;
  const totalAvailableSP = myActiveSP + (myBonusSP || 0);
  const emptySlots = myField.map((m, i) => m === null ? i : -1).filter(i => i >= 0);

  if (emptySlots.length === 0) return [];

  return myHand.filter(card => {
    if (card.type !== 'monster') return false;
    // コスト計算（常時効果による軽減を考慮）
    const cardWithMod = card as CardWithTempModifier;
    let cost = card.cost + (cardWithMod.tempCostModifier || 0);
    if (costModifierFn) {
      cost += costModifierFn(card);
    }
    return cost <= totalAvailableSP;
  });
}

/**
 * 配置可能なフィールドカードを取得
 */
export function getPlaceableFieldCards(gameState: AIGameState): CardWithId[] {
  const { myHand, myActiveSP, myBonusSP, myFieldCard } = gameState;
  const totalAvailableSP = myActiveSP + (myBonusSP || 0);

  // 既にフィールドカードが設置されている場合は空配列
  // ※上書き配置を許可する場合はこの条件を削除
  if (myFieldCard) return [];

  return myHand.filter(card => {
    if (card.type !== 'field') return false;
    return card.cost <= totalAvailableSP;
  });
}

/**
 * 配置可能なフェイズカードを取得
 */
export function getPlaceablePhaseCards(gameState: AIGameState): CardWithId[] {
  const { myHand, myActiveSP, myBonusSP, myPhaseCard } = gameState;
  const totalAvailableSP = myActiveSP + (myBonusSP || 0);

  // 既にフェイズカードが設置されている場合は空配列
  if (myPhaseCard) return [];

  return myHand.filter(card => {
    if (card.type !== 'phasecard') return false;
    return card.cost <= totalAvailableSP;
  });
}

/**
 * フェイズカードにチャージ可能なカードを取得
 * 同属性 or 「なし」属性 or フェイズカードが「なし」属性なら全カード
 */
export function getPhaseCardChargeableCards(gameState: AIGameState): CardWithId[] {
  const { myHand, myPhaseCard } = gameState;

  // フェイズカードが設置されていない、または最終段階の場合は空配列
  if (!myPhaseCard) return [];
  const currentStage = myPhaseCard.stage !== undefined ? myPhaseCard.stage : (myPhaseCard.charges?.length || 0);
  if (currentStage >= 3) return [];

  const isNoneAttribute = myPhaseCard.attribute === 'なし';

  return myHand.filter(card => {
    // チャージ可能なカード種類
    if (!['monster', 'magic', 'field', 'phase'].includes(card.type)) return false;
    // フェイズカードが「なし」属性なら全カードチャージ可能
    if (isNoneAttribute) return true;
    // 同属性 or 「なし」属性カード
    return card.attribute === myPhaseCard.attribute || card.attribute === 'なし';
  });
}

/**
 * フェイズカードにチャージ可能かどうか
 */
export function canChargeToPhaseCard(gameState: AIGameState, chargeUsedThisTurn: boolean = false): boolean {
  if (chargeUsedThisTurn) return false;
  return getPhaseCardChargeableCards(gameState).length > 0;
}

/**
 * 使用可能な魔法カードを取得
 * コスト判定後、効果発動条件もチェック（墓地条件、フィールド条件など）
 */
export function getUsableMagicCards(gameState: AIGameState, costModifierFn: CostModifierFn | null = null): CardWithId[] {
  const { myHand, myActiveSP, myBonusSP } = gameState;
  const totalAvailableSP = myActiveSP + (myBonusSP || 0);

  // コストで使用可能なカードをフィルタ
  const costFilteredCards = myHand.filter(card => {
    if (card.type !== 'magic') return false;
    const cardWithMod = card as CardWithTempModifier;
    let cost = card.cost + (cardWithMod.tempCostModifier || 0);
    if (costModifierFn) {
      cost += costModifierFn(card);
    }
    return cost <= totalAvailableSP;
  });

  // 効果発動条件でさらにフィルタ（墓地条件、フィールド条件など）
  return filterEffectiveMagicCards(costFilteredCards, gameState);
}

/**
 * 空きスロットを取得
 */
export function getEmptySlots(gameState: AIGameState): number[] {
  return gameState.myField
    .map((m, i) => m === null ? i : -1)
    .filter(i => i >= 0);
}

/**
 * チャージ可能なモンスターを取得
 * チャージ枚数が2未満で、かつ技を持っているモンスターを返す
 * 状態異常で技が使えないモンスターにはチャージしない
 */
export function getChargeableMonsters(gameState: AIGameState): ChargeableMonster[] {
  const chargeableMonsters: ChargeableMonster[] = [];
  gameState.myField.forEach((monster, index) => {
    if (!monster) return;
    const charges = monster.charges?.length || 0;
    // 技を持っていないモンスターにはチャージしない
    const hasSkill = monster.basicSkill || monster.advancedSkill;
    // 状態異常で技が使えないモンスターにはチャージしない（檻、行動不能、効果無効等）
    if (!statusEffectEngine.canUseSkill(monster)) return;
    if (charges < 2 && hasSkill) {
      chargeableMonsters.push({ monsterIndex: index, monster, currentCharges: charges });
    }
  });
  return chargeableMonsters;
}

/**
 * チャージに使用可能なカードを取得
 * モンスター、魔法、フィールドカード、フェーズカードがチャージ可能
 */
export function getChargeableCards(gameState: AIGameState): CardWithId[] {
  return gameState.myHand.filter(card => {
    return card.type === 'monster' || card.type === 'magic' || card.type === 'field' || card.type === 'phasecard';
  });
}

/**
 * 使用可能なスキルを取得
 * 1ターン1回制限（usedSkillThisTurn）もチェック
 * 状態異常チェック（檻、行動不能、効果無効等）も行う
 * スキル有効性チェック（手札条件など）も行う
 */
export function getUsableSkills(gameState: AIGameState): UsableSkill[] {
  const skills: UsableSkill[] = [];
  gameState.myField.forEach((monster, index) => {
    if (!monster) return;
    // 既に技を発動済みの場合はスキップ
    if (monster.usedSkillThisTurn) return;
    // 状態異常で技が使えないモンスターはスキップ（檻、行動不能、効果無効等）
    if (!statusEffectEngine.canUseSkill(monster)) return;
    const charges = monster.charges?.length || 0;

    if (charges >= 1 && monster.basicSkill) {
      // スキル有効性チェック（手札条件、フィールド空き条件など）
      if (isSkillEffective(monster, 'basic', gameState)) {
        skills.push({ monsterIndex: index, skillType: 'basic', monster });
      }
    }
    if (charges >= 2 && monster.advancedSkill) {
      // スキル有効性チェック（手札条件、フィールド空き条件など）
      if (isSkillEffective(monster, 'advanced', gameState)) {
        skills.push({ monsterIndex: index, skillType: 'advanced', monster });
      }
    }
  });
  return skills;
}

/**
 * 攻撃可能なモンスターのインデックスを取得
 * canAttackフラグと状態異常（凍結、眠り等）の両方をチェック
 */
export function getAttackableMonsters(gameState: AIGameState): number[] {
  return gameState.myField
    .map((m, i) => {
      if (!m || !m.canAttack) return -1;
      // 状態異常による攻撃不可をチェック（凍結、眠り、行動不能）
      if (!statusEffectEngine.canAttack(m)) return -1;
      return i;
    })
    .filter(i => i >= 0);
}

/**
 * 有効な攻撃対象を取得
 */
export function getValidAttackTargets(gameState: AIGameState): AttackTarget[] {
  const targets: AttackTarget[] = [];

  // 相手モンスター
  gameState.opponentField.forEach((monster, index) => {
    if (monster) targets.push(index);
  });

  // 直接攻撃
  targets.push('direct');

  return targets;
}

/**
 * 潜航可能な手札カードを取得
 */
export function getSubmergibleCards(gameState: AIGameState): HandCard[] {
  const { myHand, myDeepSeaZone, turn } = gameState;

  // 深海領域がない or 開放されていない場合は空配列
  if (!myDeepSeaZone || !myDeepSeaZone.isUnlocked) return [];

  const context: SubmergeContext = {
    deepSeaZone: myDeepSeaZone,
    allowWaterAttribute: false, // フェイズカード効果は別途判定
    currentTurn: turn,
  };

  return myHand.filter(card => canSubmerge(card, context)) as HandCard[];
}

/**
 * 浮上可能な深海カードを取得
 */
export function getAISurfaceableCards(gameState: AIGameState): DeepSeaCard[] {
  const { myDeepSeaZone, myField, myActiveSP, myBonusSP } = gameState;

  // 深海領域がない場合は空配列
  if (!myDeepSeaZone || !myDeepSeaZone.isUnlocked) return [];

  const emptySlots = myField.filter(m => m === null).length;
  if (emptySlots === 0) return [];

  const totalAvailableSP = myActiveSP + (myBonusSP || 0);
  return getSurfaceableCards(myDeepSeaZone, totalAvailableSP, emptySlots, 0);
}

/** getActionsRemaining用オプション */
export interface GetActionsRemainingOptions {
  costModifierFn?: CostModifierFn | null;
  chargeUsedThisTurn?: boolean;
}

/**
 * 残りアクション情報を取得
 */
export function getActionsRemaining(gameState: AIGameState, options: GetActionsRemainingOptions = {}): ActionsRemaining {
  const { costModifierFn = null, chargeUsedThisTurn = false } = options;
  const summonableCards = getSummonableCards(gameState, costModifierFn);
  const usableSkills = getUsableSkills(gameState);
  const usableMagicCards = getUsableMagicCards(gameState, costModifierFn);
  const chargeableMonsters = getChargeableMonsters(gameState);
  const chargeableCards = getChargeableCards(gameState);
  const canCharge = !chargeUsedThisTurn && chargeableMonsters.length > 0 && chargeableCards.length > 0;
  const placeableFieldCards = getPlaceableFieldCards(gameState);
  const placeablePhaseCards = getPlaceablePhaseCards(gameState);
  const phaseCardChargeableCards = getPhaseCardChargeableCards(gameState);
  const canChargePhaseCard = !chargeUsedThisTurn && phaseCardChargeableCards.length > 0;
  // 深海領域
  const submergibleCards = getSubmergibleCards(gameState);
  const surfaceableCards = getAISurfaceableCards(gameState);

  return {
    canSummon: summonableCards.length > 0,
    canUseSkill: usableSkills.length > 0,
    canUseMagic: usableMagicCards.length > 0,
    canCharge,
    canChargePhaseCard,
    canPlaceFieldCard: placeableFieldCards.length > 0,
    canPlacePhaseCard: placeablePhaseCards.length > 0,
    canActivateTrigger: false, // トリガーは外部から渡す
    // 深海領域
    canSubmerge: submergibleCards.length > 0,
    canSurface: surfaceableCards.length > 0,
    summonableCards,
    usableSkills,
    usableMagicCards,
    chargeableMonsters,
    chargeableCards,
    placeableFieldCards,
    placeablePhaseCards,
    phaseCardChargeableCards,
    // 深海領域カード
    submergibleCards,
    surfaceableCards,
  };
}

// ============================================================
// メインフェイズ処理
// ============================================================

/** メインフェイズアクション結果 */
export type MainPhaseActionResult = 'summon' | 'field' | 'phase' | 'skill' | 'magic' | 'charge' | 'phaseCharge' | 'trigger' | 'end';

/**
 * メインフェイズのAI行動を1アクション実行
 * @returns 実行したアクション種別
 */
export function executeAIMainPhaseAction(
  gameState: AIGameState,
  actions: MainPhaseActions,
  strategy: AIStrategy,
  options: MainPhaseOptions = {}
): MainPhaseActionResult {
  const { summonCard, executeSkill, activateMagicCard, activateTrigger, chargeCard, chargePhaseCard, placeFieldCard, placePhaseCard, nextPhase } = actions;
  const { costModifierFn, activatableTriggers = [], chargeUsedThisTurn = false } = options;

  // 残りアクションを取得
  const remaining = getActionsRemaining(gameState, { costModifierFn, chargeUsedThisTurn });

  // 1. フィールドカード配置判断（召喚前に判断、シナジーを活かすため）
  if (remaining.canPlaceFieldCard && placeFieldCard) {
    const fieldCardToPlace = strategy.chooseFieldCard(remaining.placeableFieldCards, gameState);
    if (fieldCardToPlace) {
      placeFieldCard(fieldCardToPlace);
      return 'field';
    }
  }

  // 2. フェイズカード配置判断
  if (remaining.canPlacePhaseCard && placePhaseCard) {
    const phaseCardToPlace = strategy.choosePhaseCard(remaining.placeablePhaseCards, gameState);
    if (phaseCardToPlace) {
      placePhaseCard(phaseCardToPlace);
      return 'phase';
    }
  }

  // 3. 魔法カード使用判断（召喚前：リクルート・蘇生・儀式系のみ）
  // 召喚前に使うとSPを消費して召喚の阻害になるため、場にモンスターを出す魔法のみ
  const preSummonMagics = remaining.usableMagicCards.filter(card => PRE_SUMMON_MAGIC_IDS.has(card.id));
  const magicToUse = strategy.chooseMagicCard(preSummonMagics, gameState);
  if (magicToUse && activateMagicCard) {
    activateMagicCard(magicToUse);
    return 'magic';
  }

  // 4. 召喚判断
  const cardToSummon = strategy.chooseSummon(remaining.summonableCards, gameState);
  if (cardToSummon) {
    const emptySlots = getEmptySlots(gameState);
    const slot = strategy.chooseSlot(emptySlots, cardToSummon, gameState);
    if (slot !== null && slot !== undefined) {
      summonCard(cardToSummon, slot);
      return 'summon';
    }
  }

  // 5. モンスターチャージ判断（スキル使用前に行う）
  if (remaining.canCharge && chargeCard) {
    const chargeDecision = strategy.chooseCharge(
      remaining.chargeableCards,
      remaining.chargeableMonsters,
      gameState
    );
    if (chargeDecision) {
      chargeCard(chargeDecision.card, chargeDecision.monsterIndex);
      return 'charge';
    }
  }

  // 6. フェイズカードチャージ判断（モンスターチャージの後）
  if (remaining.canChargePhaseCard && chargePhaseCard) {
    const phaseChargeDecision = strategy.choosePhaseCardCharge(
      remaining.phaseCardChargeableCards,
      gameState
    );
    if (phaseChargeDecision) {
      chargePhaseCard(phaseChargeDecision);
      return 'phaseCharge';
    }
  }

  // 7. スキル使用判断
  const skillToUse = strategy.chooseSkill(remaining.usableSkills, gameState);
  if (skillToUse) {
    executeSkill(skillToUse.monsterIndex, skillToUse.skillType);
    return 'skill';
  }

  // 8. トリガー発動判断
  const triggerToActivate = strategy.chooseTrigger(activatableTriggers, gameState);
  if (triggerToActivate && activateTrigger) {
    activateTrigger(triggerToActivate);
    return 'trigger';
  }

  // 9. バトルフェイズ前にSP余りがあれば魔法を確定使用（刹那詠唱がなければSPは無駄になる）
  // remaining を再取得（召喚等でSP状況が変わっている可能性）
  const remainingForMagic = getActionsRemaining(gameState, { costModifierFn, chargeUsedThisTurn });
  if (remainingForMagic.usableMagicCards.length > 0 && activateMagicCard) {
    // 使える魔法があれば確定で使用
    activateMagicCard(remainingForMagic.usableMagicCards[0]);
    return 'magic';
  }

  // 10. アクションがなければバトルフェイズへ
  nextPhase();
  return 'end';
}

// ============================================================
// バトルフェイズ処理
// ============================================================

/** バトルフェイズアクション結果 */
export type BattlePhaseActionResult = 'attack' | 'skip' | 'end';

/** バトルフェイズ実行結果 */
export interface BattlePhaseResult {
  action: BattlePhaseActionResult;
  attackedMonsters: Set<number>;
}

/**
 * バトルフェイズのAI行動を1アクション実行
 * @param attackedMonsters - 既に攻撃したモンスターのインデックスセット
 * @returns 実行結果
 */
export function executeAIBattlePhaseAction(
  gameState: AIGameState,
  actions: BattlePhaseActions,
  strategy: AIStrategy,
  attackedMonsters: Set<number> = new Set()
): BattlePhaseResult {
  const { attack, nextPhase } = actions;

  // 先攻1ターン目は攻撃スキップ
  if (gameState.isFirstTurn && gameState.currentPlayer === gameState.firstPlayer) {
    nextPhase();
    return { action: 'end', attackedMonsters };
  }

  // 攻撃可能なモンスターを取得（既に攻撃したものを除く）
  const allAttackable = getAttackableMonsters(gameState);
  const remainingAttackers = allAttackable.filter(idx => !attackedMonsters.has(idx));

  // 全員攻撃済みなら終了
  if (remainingAttackers.length === 0) {
    nextPhase();
    return { action: 'end', attackedMonsters };
  }

  // 攻撃順序を決定
  const attackOrder = strategy.chooseAttackOrder(remainingAttackers, gameState);
  if (attackOrder.length === 0) {
    nextPhase();
    return { action: 'end', attackedMonsters };
  }

  // 最初のモンスターで攻撃
  const attackerIndex = attackOrder[0];
  const attacker = gameState.myField[attackerIndex];

  // canAttackフラグと状態異常の両方をチェック
  if (attacker && attacker.canAttack && statusEffectEngine.canAttack(attacker)) {
    const validTargets = getValidAttackTargets(gameState);
    const target = strategy.chooseAttackTarget(validTargets, attacker, gameState);

    attack(attackerIndex, target);
    attackedMonsters.add(attackerIndex);

    return { action: 'attack', attackedMonsters };
  }

  // 攻撃できない場合（状態異常含む）は攻撃済みとしてマーク
  if (attacker) {
    attackedMonsters.add(attackerIndex);
    // まだ他に攻撃可能なモンスターがいる可能性があるので継続
    if (attackOrder.length > 1) {
      return { action: 'skip', attackedMonsters };
    }
  }

  // 攻撃できるモンスターがいない場合は終了
  nextPhase();
  return { action: 'end', attackedMonsters };
}

// ============================================================
// 特殊ケース処理
// ============================================================

/**
 * 手札選択（効果による）のAI処理
 */
export function handleAIHandSelection(
  pendingHandSelection: PendingHandSelection,
  gameState: AIGameState,
  strategy: AIStrategy
): boolean {
  const { callback, filter, message = '' } = pendingHandSelection;
  // undefined/null を除外してからフィルタ適用
  const validCards = gameState.myHand
    .filter((card): card is CardWithId => card != null)
    .filter(filter || (() => true));

  if (validCards.length === 0) {
    console.warn('AI: 選択可能な手札がありません');
    // 選択できない場合はnullでコールバック（キャンセル扱い）
    callback(null);
    return false;
  }

  const selectedCard = strategy.chooseFromHand(validCards, message, gameState);
  if (selectedCard) {
    // 配列で渡す（UI側の型に合わせる）
    callback([selectedCard]);
    return true;
  }

  // ランダムで選択（配列で渡す）
  callback([validCards[0]]);
  return true;
}

/**
 * モンスターターゲット選択のAI処理
 */
export function handleAIMonsterTarget(
  pendingMonsterTarget: PendingMonsterTarget,
  gameState: AIGameState,
  strategy: AIStrategy
): boolean {
  const { callback, targetPlayer, message = '' } = pendingMonsterTarget;

  const targetField = targetPlayer === 'opponent'
    ? gameState.opponentField
    : gameState.myField;

  const validIndices = targetField
    .map((m, i) => m ? i : -1)
    .filter(i => i >= 0);

  if (validIndices.length === 0) {
    console.warn('AI: 選択可能なモンスターがいません');
    callback(null);
    return false;
  }

  const selectedIndex = strategy.chooseMonsterTarget(validIndices, message, gameState);
  if (selectedIndex !== null && selectedIndex !== undefined) {
    callback(selectedIndex);
    return true;
  }

  // ランダムで選択
  callback(validIndices[0]);
  return true;
}

/**
 * 墓地選択のAI処理
 */
export function handleAIGraveyardSelection(
  pendingGraveyardSelection: PendingGraveyardSelection,
  gameState: AIGameState,
  strategy: AIStrategy
): boolean {
  const { callback, filter, message = '' } = pendingGraveyardSelection;
  // undefined/null を除外してからフィルタ適用
  const validCards = gameState.myGraveyard
    .filter((card): card is CardWithId => card != null)
    .filter(filter || (() => true));

  if (validCards.length === 0) {
    console.warn('AI: 選択可能な墓地カードがありません');
    callback(null);
    return false;
  }

  const selectedCard = strategy.chooseFromGraveyard(validCards, message, gameState);
  if (selectedCard) {
    // 配列で渡す（UI側の型に合わせる）
    callback([selectedCard]);
    return true;
  }

  // ランダムで選択（配列で渡す）
  callback([validCards[0]]);
  return true;
}

/**
 * デッキ確認のAI処理
 */
export function handleAIDeckReview(
  pendingDeckReview: PendingDeckReview,
  gameState: AIGameState,
  strategy: AIStrategy
): boolean {
  const { cards, onConfirm, onSelect, selectMode } = pendingDeckReview;

  const result = strategy.chooseFromDeckReview(cards, { selectMode }, gameState);

  if (selectMode && onSelect && result.selectedCards && result.remainingCards) {
    // onSelect(selectedCards, remainingCards) の形式で呼び出し
    onSelect(result.selectedCards, result.remainingCards);
    return true;
  }

  if (onConfirm) {
    onConfirm(result.reorderedCards || cards);
    return true;
  }

  return false;
}

/**
 * 刹那詠唱チェーン確認のAI処理
 */
export function handleAIChainConfirmation(
  chainConfirmation: ChainConfirmation,
  availableSetsunaCards: CardWithId[],
  gameState: AIGameState,
  strategy: AIStrategy,
  actions: ChainActions
): boolean {
  const { skipChainConfirmation, activateSetsunaInChain } = actions;
  const { context } = chainConfirmation;

  const cardToActivate = strategy.chooseSetsuna(
    availableSetsunaCards,
    context || {},
    gameState
  );

  if (cardToActivate) {
    activateSetsunaInChain(cardToActivate);
    return true;
  }

  skipChainConfirmation();
  return false;
}

// ============================================================
// エクスポート
// ============================================================

export { getStrategy };
